import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, Form, FormikValues } from 'formik';
import * as yup from 'yup';

import Field from 'components/FieldWithoutGrid';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { htmlT } from 'utils/generic';

import {
  Container,
  Header,
  FieldsContainer,
  Input,
  BackButton,
  ResendButton,
  NextButton,
  ButtonsContainer,
  ButtonLine,
} from '../../styles';
import { OTPChannel, TelephoneValues } from '../Telephone';
import { VerifyMessage } from './styles';

export interface VerificationCodeValues {
  code: string;
}

interface Props {
  values: VerificationCodeValues | null;
  onSubmit: (values: VerificationCodeValues) => void;
  onResend: () => Promise<any> | undefined;
  telephone: TelephoneValues | null;
  channel?: OTPChannel;
}

const CODE_LENGTH_PER_CHANNEL: Record<OTPChannel, number> = {
  sms: 4,
  whatsapp: 6,
};

const VerificationCode = ({ values, onSubmit, onResend, telephone, channel = 'whatsapp' }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const formRef = useRef<any>();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  if (!telephone) return null;

  const codeLength = CODE_LENGTH_PER_CHANNEL[channel];

  const validationSchema = yup.object().shape({
    code: yup.string().length(codeLength).required(),
  });

  const initialValues: VerificationCodeValues = {
    code: '',
  };

  const handleBack = () => history.goBack();

  const handleSubmit = async (formValues: VerificationCodeValues) => {
    setLoading(true);
    await onSubmit(formValues);
    setLoading(false);
  };

  const handleResend = async () => {
    setResendLoading(true);
    await onResend();
    if (formRef.current) {
      formRef.current.setFieldValue('code', '');
    }
    setResendLoading(false);
  };

  return (
    <Formik
      innerRef={formRef}
      initialStatus={{ rightCheckIfIsValid: true }}
      initialValues={values || initialValues}
      isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid }: FormikValues) => (
        <Form>
          <Container>
            <Header>{t('invite.verificationCode.title')}</Header>
            <VerifyMessage>
              {htmlT(t, 'invite.verificationCode.subtitle', {
                countryCode: telephone.country_code.code,
                phone: telephone.phone,
              })}
            </VerifyMessage>

            <FieldsContainer>
              <Field name="code">
                <Input
                  placeholder={t('invite.verificationCode.placeholder')}
                  autoCapitalize="off"
                  autoCorrect="off"
                  maxLength={codeLength}
                  type="tel"
                />
              </Field>
            </FieldsContainer>

            <ResendButton type="button" onClick={handleResend} disabled={resendLoading} loading={resendLoading}>
              {t('invite.verificationCode.resendCode')}
            </ResendButton>

            <ButtonsContainer>
              <ButtonLine>
                <BackButton type="button" onClick={handleBack}>
                  {t('invite.backButton')}
                </BackButton>
                <NextButton disabled={!isValid || loading} loading={loading}>
                  {t('invite.verificationCode.verifyButton')}
                </NextButton>
              </ButtonLine>
            </ButtonsContainer>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

export default VerificationCode;
